import React, { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";

const COLOR_MAP = {
  sky: { bg: "bg-[#dbeafe]", border: "border-[#1E6FE0]/45", num: "text-[#1E40AF]", label: "text-[#1E40AF]" },
  lightBlue: { bg: "bg-[#e0f2ff]", border: "border-[#7DBEFF]/55", num: "text-[#1E6FE0]", label: "text-[#1E6FE0]" },
  green: { bg: "bg-[#dcfce7]", border: "border-[#16A34A]/50", num: "text-[#166534]", label: "text-[#166534]" },
  pink: { bg: "bg-[#fbe1ea]", border: "border-[#e85a8e]/40", num: "text-[#a8195a]", label: "text-[#a8195a]" },
  red: { bg: "bg-[#fee2e2]", border: "border-[#DC2626]/50", num: "text-[#991B1B]", label: "text-[#991B1B]" },
  yellow: { bg: "bg-[#fef9c3]", border: "border-[#EAB308]/55", num: "text-[#854D0E]", label: "text-[#854D0E]" },
  amber: { bg: "bg-[#fef9c3]", border: "border-[#EAB308]/55", num: "text-[#854D0E]", label: "text-[#854D0E]" },
  orange: { bg: "bg-[#ffedd5]", border: "border-[#F97316]/50", num: "text-[#9A3412]", label: "text-[#9A3412]" },
  purple: { bg: "bg-[#ede4ff]", border: "border-[#7C3AED]/50", num: "text-[#5B21B6]", label: "text-[#5B21B6]" },
  white: { bg: "bg-card/80", border: "border-border", num: "text-foreground", label: "text-muted-foreground" },
  default: { bg: "bg-card/80", border: "border-border", num: "text-foreground", label: "text-muted-foreground" },
};

// Map color theme to an accent ring color (for pulse animation)
const PULSE_RING_COLOR = {
  sky: "#1E6FE0",
  lightBlue: "#7DBEFF",
  green: "#16A34A",
  pink: "#e85a8e",
  red: "#DC2626",
  yellow: "#EAB308",
  amber: "#EAB308",
  orange: "#F97316",
  purple: "#7C3AED",
  white: "#2A4128",
  default: "#2A4128",
};

export default function KPITiles({ items }) {
  // Track previous numeric values per-label to detect increases
  const prevRef = useRef({});
  const [pulseMap, setPulseMap] = useState({});

  useEffect(() => {
    const next = { ...pulseMap };
    const timers = [];
    let changed = false;

    items.forEach((it) => {
      const v = typeof it.value === "number" ? it.value : Number(it.value);
      const prev = prevRef.current[it.label];
      if (typeof prev === "number" && Number.isFinite(v) && v > prev) {
        next[it.label] = (next[it.label] || 0) + 1; // bump key to retrigger animation
        changed = true;
        const key = it.label;
        const t = setTimeout(() => {
          setPulseMap((m) => {
            const c = { ...m };
            delete c[key];
            return c;
          });
        }, 2200); // matches CSS animation duration
        timers.push(t);
      }
      if (Number.isFinite(v)) prevRef.current[it.label] = v;
    });

    if (changed) setPulseMap(next);
    return () => timers.forEach((t) => clearTimeout(t));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items.map((i) => `${i.label}:${i.value}`).join("|")]);

  return (
    <div
      className="kpi-tiles-grid"
      style={{ "--kpi-count": items.length }}
      data-testid="kpi-tiles"
    >
      {items.map((it) => {
        const c = COLOR_MAP[it.color] || COLOR_MAP.default;
        const ring = PULSE_RING_COLOR[it.color] || PULSE_RING_COLOR.default;
        const pulseKey = pulseMap[it.label];
        const pulsing = Boolean(pulseKey);
        const baseClass = `relative p-3.5 ${c.border} ${c.bg} shadow-sm rounded-xl border min-w-0 ${pulsing ? "kpi-pulse" : ""}`;
        const inner = (
          <>
            <div className={`text-[10px] uppercase tracking-wider font-medium truncate ${c.label}`}>{it.label}</div>
            <div
              key={pulseKey || "static"}
              className={`mt-1.5 font-mono-num font-semibold text-2xl leading-none ${c.num} ${pulsing ? "kpi-value-bump" : ""}`}
            >
              {it.value ?? "—"}
            </div>
            {it.hint && <div className="mt-1 text-[11px] text-muted-foreground truncate">{it.hint}</div>}
          </>
        );
        const testId = `kpi-${it.label.toLowerCase().replace(/\s+/g, "-")}`;
        const interactiveCls = "block transition-shadow hover:shadow-md hover:-translate-y-0.5 transition-transform cursor-pointer";
        const style = pulsing ? { "--kpi-pulse-ring": ring } : undefined;
        if (it.to) {
          return (
            <Link key={it.label} to={it.to} className={`${baseClass} ${interactiveCls}`} style={style} data-testid={testId} data-pulsing={pulsing || undefined}>
              {inner}
            </Link>
          );
        }
        if (it.onClick) {
          return (
            <button
              key={it.label}
              type="button"
              onClick={it.onClick}
              className={`${baseClass} ${interactiveCls} text-left`}
              style={style}
              data-testid={testId}
              data-pulsing={pulsing || undefined}
            >
              {inner}
            </button>
          );
        }
        return (
          <Card key={it.label} className={baseClass} style={style} data-testid={testId} data-pulsing={pulsing || undefined}>{inner}</Card>
        );
      })}
    </div>
  );
}
